import { useEffect, useMemo, useRef, useState } from "react";
import {
  AlertTriangle,
  CheckCircle2,
  FileCheck2,
  Loader2,
  Plus,
  Search,
  ShoppingBag,
  Trash2,
  UserRound,
  X,
} from "lucide-react";

import { Badge, Button } from "@/design-system/primitives";
import { PrescriptionAuthorization } from "./PrescriptionAuthorization";
import {
  useClinicianPostVisitReview,
  useClinicianTemplates,
  useSubmitClinicianPostVisitReview,
  useWorklistPatientBrief,
} from "@/lib/api/clinicianHooks";
import type {
  AppointmentWorkItem,
  ClinicianTemplate,
  PatientBriefResponse,
  PostVisitReviewResponse,
} from "@/types/clinician";
import {
  additionalPostVisitRow,
  isFinishedPostVisitRow,
  postVisitProductMatchesSearch,
  postVisitRowError,
  postVisitRows,
  toPostVisitSubmitItem,
  updatePostVisitMedication,
  type PostVisitDraftRow,
} from "./postVisitReview";

const DEFAULT_PATIENT_MESSAGE =
  "Your clinician approved additional treatment options after your visit. Sign in to review the details and purchase any treatment you would like to start.";

function reviewVersion(review: PostVisitReviewResponse) {
  return JSON.stringify([
    review.review.id,
    review.review.updated_at,
    review.review.patient_message_status,
    ...review.purchased_items.map((item) => item.order_id),
    ...review.authorizations.map((authorization) => [
      authorization.id,
      authorization.updated_at,
      authorization.status,
    ]),
  ]);
}

function patientName(appointment: AppointmentWorkItem) {
  return (
    appointment.patient.display_name ||
    appointment.patient.email ||
    "Unknown patient"
  );
}

export function PostVisitReviewModal({
  appointment,
  onClose,
}: {
  appointment: AppointmentWorkItem;
  onClose: () => void;
}) {
  const review = useClinicianPostVisitReview(
    appointment.id,
    appointment.patient.id,
  );
  const templates = useClinicianTemplates("physician_note");
  const brief = useWorklistPatientBrief(appointment.patient.id);
  const submitReview = useSubmitClinicianPostVisitReview();
  const [rows, setRows] = useState<PostVisitDraftRow[]>([]);
  const [catalogSearch, setCatalogSearch] = useState("");
  const [patientMessage, setPatientMessage] = useState(DEFAULT_PATIENT_MESSAGE);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const initializedReview = useRef<string | null>(null);
  const displayName = patientName(appointment);

  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape" && !submitReview.isPending) onClose();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [onClose, submitReview.isPending]);

  useEffect(() => {
    if (!review.data || !templates.isFetched) return;
    const version = reviewVersion(review.data);
    if (initializedReview.current === version) return;
    initializedReview.current = version;
    setRows(postVisitRows(review.data, templates.data ?? [], displayName));
    setPatientMessage(
      review.data.review.patient_message ?? DEFAULT_PATIENT_MESSAGE,
    );
  }, [displayName, review.data, templates.data, templates.isFetched]);

  const selectedProductIds = useMemo(
    () => new Set(rows.map((row) => row.purchaseProductId)),
    [rows],
  );
  const catalogResults = useMemo(() => {
    const search = catalogSearch.trim().toLocaleLowerCase();
    if (!search) return [];
    return (review.data?.catalog ?? [])
      .filter(
        (product) =>
          !selectedProductIds.has(product.purchase_product_id) &&
          postVisitProductMatchesSearch(product, search),
      )
      .slice(0, 8);
  }, [catalogSearch, review.data?.catalog, selectedProductIds]);
  const additionalRows = rows.filter((row) => row.source === "additional");
  const unfinishedRows = rows.filter((row) => !isFinishedPostVisitRow(row));
  const hasAdditionalApproval = additionalRows.some(
    (row) => row.action === "authorize_for_purchase",
  );

  const changeRow = (
    clientKey: string,
    update:
      | Partial<PostVisitDraftRow>
      | ((row: PostVisitDraftRow) => PostVisitDraftRow),
  ) => {
    setRows((current) =>
      current.map((row) => {
        if (row.clientKey !== clientKey) return row;
        if (row.persisted) return row;
        return typeof update === "function"
          ? update(row)
          : { ...row, ...update };
      }),
    );
    setError(null);
    setSuccess(null);
  };

  const submit = async () => {
    if (
      !review.data ||
      !templates.isFetched ||
      initializedReview.current !== reviewVersion(review.data)
    )
      return;
    setError(null);
    setSuccess(null);
    if (!unfinishedRows.length && review.data?.review.status === "completed") {
      setSuccess("All selected treatments are complete.");
      return;
    }
    const firstInvalid = unfinishedRows
      .map((row) => ({ row, message: postVisitRowError(row) }))
      .find((entry) => entry.message);
    if (firstInvalid) {
      setError(`${firstInvalid.row.label}: ${firstInvalid.message}`);
      return;
    }
    if (hasAdditionalApproval && !patientMessage.trim()) {
      setError(
        "Add the patient message that explains how to review and purchase the approved treatment.",
      );
      return;
    }
    try {
      const result = await submitReview.mutateAsync({
        appointmentId: appointment.id,
        body: {
          patient_id: appointment.patient.id,
          confirmed: true,
          items: unfinishedRows.map(toPostVisitSubmitItem),
          patient_message: hasAdditionalApproval ? patientMessage.trim() : null,
        },
      });
      initializedReview.current = reviewVersion(result);
      setRows(postVisitRows(result, templates.data ?? [], displayName));
      setPatientMessage(
        result.review.patient_message ?? DEFAULT_PATIENT_MESSAGE,
      );
      const failures = result.items.filter((item) => item.status === "failed");
      if (failures.length) {
        setError(
          `${failures.length} treatment${failures.length === 1 ? "" : "s"} did not finish. The exact saved row is locked; retry to resume safely, or contact operations if its configuration must change.`,
        );
      } else if (result.patient_message_error) {
        setError(
          `The clinical work was saved, but patient-message delivery could not be confirmed: ${result.patient_message_error}. Check the Healthie conversation or contact operations to reconcile delivery before sending another message.`,
        );
      } else {
        setSuccess(
          result.items.length
            ? "Visit review saved. Paid prescriptions were written and additional approvals are ready for the patient to purchase."
            : "Visit review completed with no treatment changes.",
        );
      }
    } catch (failure) {
      setError(
        failure instanceof Error
          ? failure.message
          : "The visit review did not finish. Nothing successful was discarded; retry to resume safely.",
      );
    }
  };

  return (
    <div
      className="fixed inset-0 z-[130] bg-ink/55 backdrop-blur-sm p-8 md:p-20 overflow-y-auto"
      role="dialog"
      aria-modal="true"
      aria-labelledby="post-visit-review-title"
    >
      <div className="min-h-full flex items-start justify-center">
        <section className="w-full max-w-[118rem] bg-page border border-line shadow-xl my-8 max-h-[calc(100vh-2rem)] overflow-hidden flex flex-col">
          <header className="px-14 py-12 border-b border-line-subtle bg-raised flex items-start justify-between gap-10">
            <div>
              <p className="type-label-xs text-success">POST-VISIT REVIEW</p>
              <h2 id="post-visit-review-title" className="type-heading-lg mt-3">
                Complete visit · {displayName}
              </h2>
              <p className="type-body-sm text-ink-muted mt-3 max-w-[70rem]">
                Finish every paid prescription now, and optionally authorize
                additional treatments discussed during the visit. Nothing is
                sent to a pharmacy until an additional treatment is purchased.
              </p>
            </div>
            <button
              type="button"
              aria-label="Close post-visit review"
              disabled={submitReview.isPending}
              onClick={onClose}
              className="p-5 text-ink-muted hover:text-ink disabled:opacity-40"
            >
              <X size={18} />
            </button>
          </header>

          {review.isLoading || templates.isLoading ? (
            <div className="min-h-[32rem] flex items-center justify-center gap-8 type-body-sm text-ink-muted">
              <Loader2 size={18} className="animate-spin" /> Loading visit and
              treatment catalog…
            </div>
          ) : review.isError || !review.data ? (
            <div className="m-14 border border-danger/30 bg-danger-wash p-14 text-danger">
              <p className="type-label-sm">Visit review could not be loaded</p>
              <p className="type-body-sm mt-3">
                The appointment was not changed. Close and retry from the visit
                row.
              </p>
            </div>
          ) : (
            <div className="grid lg:grid-cols-[minmax(0,1.7fr)_minmax(23rem,0.7fr)] min-h-0 overflow-hidden">
              <div className="min-w-0 overflow-y-auto p-14 flex flex-col gap-14">
                <section className="flex flex-col gap-8">
                  <div className="flex items-center justify-between gap-8">
                    <div>
                      <p className="type-label-sm font-medium">
                        Treatments reviewed
                      </p>
                      <p className="text-11 text-ink-muted mt-2">
                        Paid prescriptions awaiting a decision start here. “Skip
                        for now” leaves that item in the normal prescribing
                        queue.
                      </p>
                    </div>
                    <Badge
                      tone={
                        review.data.review.status === "completed"
                          ? "success"
                          : "warning"
                      }
                      size="sm"
                    >
                      {review.data.review.status.replaceAll("_", " ")}
                    </Badge>
                  </div>

                  {rows.length ? (
                    <div className="flex flex-col gap-10">
                      {rows.map((row, index) => (
                        <TreatmentRow
                          key={row.clientKey}
                          row={row}
                          index={index}
                          templates={templates.data ?? []}
                          patientName={displayName}
                          onChange={(update) =>
                            changeRow(row.clientKey, update)
                          }
                          onRemove={
                            row.source === "additional" && !row.persisted
                              ? () =>
                                  setRows((current) =>
                                    current.filter(
                                      (entry) =>
                                        entry.clientKey !== row.clientKey,
                                    ),
                                  )
                              : undefined
                          }
                        />
                      ))}
                    </div>
                  ) : (
                    <div className="border border-line-subtle bg-sunken p-12 type-body-sm text-ink-muted">
                      No paid prescription is awaiting a decision. You can
                      complete the review with no changes or authorize another
                      treatment discussed on the call.
                    </div>
                  )}
                </section>

                <section className="border-t border-line-subtle pt-14 flex flex-col gap-8">
                  <div className="flex items-center gap-7">
                    <Plus size={15} />
                    <div>
                      <p className="type-label-sm font-medium">
                        Add another approved treatment
                      </p>
                      <p className="text-11 text-ink-muted mt-2">
                        Search only live, configured treatments. The exact
                        medication tier and directions are required.
                      </p>
                    </div>
                  </div>
                  <label className="relative block">
                    <Search
                      size={14}
                      aria-hidden
                      className="absolute left-10 top-1/2 -translate-y-1/2 text-ink-muted"
                    />
                    <input
                      type="search"
                      value={catalogSearch}
                      onChange={(event) => setCatalogSearch(event.target.value)}
                      placeholder="Search treatments…"
                      className="w-full min-h-40 pl-34 pr-10 border border-line bg-raised type-body-sm outline-none focus:shadow-focus"
                    />
                  </label>
                  {catalogSearch.trim() && (
                    <div className="border border-line-subtle divide-y divide-line-subtle bg-raised">
                      {catalogResults.length ? (
                        catalogResults.map((product) => (
                          <button
                            key={product.purchase_product_id}
                            type="button"
                            onClick={() => {
                              setRows((current) => [
                                ...current,
                                additionalPostVisitRow(
                                  product,
                                  templates.data ?? [],
                                  displayName,
                                ),
                              ]);
                              setCatalogSearch("");
                            }}
                            className="w-full p-10 text-left hover:bg-sunken flex items-center justify-between gap-8"
                          >
                            <span className="type-body-sm font-medium">
                              {product.label}
                            </span>
                            <span className="type-label-xs text-ink-muted">
                              {product.medication_options.length} option
                              {product.medication_options.length === 1
                                ? ""
                                : "s"}
                            </span>
                          </button>
                        ))
                      ) : (
                        <p className="p-10 type-body-sm text-ink-muted">
                          No unselected live treatment matches that search.
                        </p>
                      )}
                    </div>
                  )}
                </section>

                {hasAdditionalApproval && (
                  <label className="border-t border-line-subtle pt-14 flex flex-col gap-5">
                    <span className="type-label-sm font-medium">
                      Patient purchase message
                    </span>
                    <span className="text-11 text-ink-muted">
                      Sent only after the additional Healthie prescription
                      record and purchase authorization succeed.
                    </span>
                    <textarea
                      rows={4}
                      value={patientMessage}
                      disabled={Boolean(review.data.review.patient_message)}
                      onChange={(event) =>
                        setPatientMessage(event.target.value)
                      }
                      className="border border-line bg-raised p-10 type-body-sm outline-none focus:shadow-focus resize-y disabled:bg-sunken"
                    />
                    {review.data.review.patient_message && (
                      <span className="text-11 text-ink-muted">
                        This exact message is saved for retry and can no longer
                        be changed.
                      </span>
                    )}
                  </label>
                )}

                {error && (
                  <div
                    role="alert"
                    className="border border-danger/30 bg-danger-wash p-10 text-danger flex gap-7"
                  >
                    <AlertTriangle size={15} className="shrink-0 mt-1" />
                    <p className="type-body-sm">{error}</p>
                  </div>
                )}
                {success && (
                  <div className="border border-success/30 bg-success-wash p-10 text-success flex gap-7">
                    <CheckCircle2 size={15} className="shrink-0 mt-1" />
                    <p className="type-body-sm">{success}</p>
                  </div>
                )}
              </div>

              <PatientContext
                appointment={appointment}
                review={review.data}
                brief={brief.data}
              />
            </div>
          )}

          <footer className="px-14 py-11 border-t border-line-subtle bg-raised flex flex-wrap items-center justify-between gap-8">
            <p className="text-11 text-ink-muted max-w-[60rem]">
              This is a recoverable batch. A failed row does not roll back or
              duplicate a successful prescription; retry submits unfinished rows
              only.
            </p>
            <div className="flex gap-7">
              <Button
                type="button"
                variant="secondary"
                disabled={submitReview.isPending}
                onClick={onClose}
              >
                Close
              </Button>
              <Button
                type="button"
                loading={submitReview.isPending}
                disabled={
                  review.isLoading ||
                  review.isError ||
                  !review.data ||
                  !templates.isFetched ||
                  initializedReview.current !== reviewVersion(review.data) ||
                  review.data?.review.status === "completed"
                }
                onClick={() => void submit()}
              >
                <FileCheck2 size={14} aria-hidden />
                {unfinishedRows.some((row) => row.persisted)
                  ? "Retry unfinished"
                  : rows.length
                    ? "Complete visit review"
                    : "Complete — no changes"}
              </Button>
            </div>
          </footer>
        </section>
      </div>
    </div>
  );
}

function TreatmentRow({
  row,
  index,
  templates,
  patientName,
  onChange,
  onRemove,
}: {
  row: PostVisitDraftRow;
  index: number;
  templates: ClinicianTemplate[];
  patientName: string;
  onChange: (
    update:
      | Partial<PostVisitDraftRow>
      | ((row: PostVisitDraftRow) => PostVisitDraftRow),
  ) => void;
  onRemove?: () => void;
}) {
  const finished = isFinishedPostVisitRow(row);
  const locked = finished || row.persisted;
  const medication = row.medicationOptions.find(
    (option) => option.product_id === row.medicationProductId,
  );
  const directionTemplates = medication?.direction_templates ?? [];
  const validation = postVisitRowError(row);
  const statusTone =
    row.status === "failed"
      ? "danger"
      : finished
        ? "success"
        : row.source === "purchased"
          ? "info"
          : "warning";

  return (
    <article className="border border-line-subtle bg-raised">
      <div className="px-11 py-9 bg-sunken border-b border-line-subtle flex items-start justify-between gap-8">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-6">
            <span className="type-label-xs text-ink-muted">
              {String(index + 1).padStart(2, "0")}
            </span>
            <p className="type-body-sm font-medium">{row.label}</p>
            <Badge tone={statusTone} size="sm">
              {row.status === "pending"
                ? row.source === "purchased"
                  ? "paid"
                  : "not purchased"
                : row.status}
            </Badge>
          </div>
          <p className="text-11 text-ink-muted mt-3">
            {row.source === "purchased"
              ? `Paid order ${row.orderId ?? ""}`
              : "Authorization will be held until this patient purchases"}
          </p>
        </div>
        {onRemove && !locked && (
          <button
            type="button"
            onClick={onRemove}
            className="p-4 text-ink-muted hover:text-danger"
            aria-label={`Remove ${row.label}`}
          >
            <Trash2 size={14} />
          </button>
        )}
      </div>

      <div
        className={`p-11 flex flex-col gap-10 ${finished ? "opacity-65" : ""}`}
      >
        {row.persisted && !finished && (
          <p className="border border-info/30 bg-info-wash p-9 type-body-sm text-ink-secondary">
            This row is saved. Retry uses the exact same medication, note, and
            directions so it cannot create a duplicate clinical record.
          </p>
        )}

        {row.source === "purchased" && !locked && (
          <div className="grid grid-cols-2 gap-6">
            <ActionButton
              active={row.action === "prescribe_now"}
              label="Write prescription now"
              onClick={() => onChange({ action: "prescribe_now", error: null })}
            />
            <ActionButton
              active={row.action === "skip"}
              label="Skip for now"
              onClick={() => onChange({ action: "skip", error: null })}
            />
          </div>
        )}

        {row.action !== "skip" && (
          <>
            <label className="flex flex-col gap-4 type-label-xs text-ink-muted">
              EXACT MEDICATION / DOSE
              <select
                value={row.medicationProductId}
                disabled={locked}
                onChange={(event) =>
                  onChange((current) =>
                    updatePostVisitMedication(
                      current,
                      event.target.value,
                      templates,
                      patientName,
                    ),
                  )
                }
                className="min-h-40 border border-line bg-raised px-10 type-body-sm text-ink disabled:bg-sunken"
              >
                <option value="">Choose exact medication / dose…</option>
                {row.medicationOptions.map((option) => (
                  <option key={option.product_id} value={option.product_id}>
                    {option.label}
                    {option.medication && option.medication !== option.label
                      ? ` · ${option.medication}`
                      : ""}
                  </option>
                ))}
              </select>
            </label>

            {medication && (
              <>
                <div className="grid md:grid-cols-[minmax(0,1fr)_minmax(10rem,0.35fr)] gap-8">
                  <label className="flex flex-col gap-4 type-label-xs text-ink-muted">
                    DIRECTIONS TEMPLATE
                    <select
                      value={row.directionTemplateId}
                      disabled={locked}
                      onChange={(event) => {
                        const selected = directionTemplates.find(
                          (template) => template.id === event.target.value,
                        );
                        onChange({
                          directionTemplateId: event.target.value,
                          directions: selected?.content ?? row.directions,
                          status: "pending",
                          error: null,
                        });
                      }}
                      className="min-h-40 border border-line bg-raised px-10 type-body-sm text-ink disabled:bg-sunken"
                    >
                      <option value="">Medication default</option>
                      {directionTemplates.map((template) => (
                        <option key={template.id} value={template.id}>
                          {template.is_default ? "Default · " : ""}
                          {template.name}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className="flex flex-col gap-4 type-label-xs text-ink-muted">
                    DOSE (OPTIONAL)
                    <input
                      value={row.dosage}
                      disabled={locked}
                      onChange={(event) =>
                        onChange({
                          dosage: event.target.value,
                          status: "pending",
                        })
                      }
                      placeholder="e.g. 5 mg"
                      className="min-h-40 border border-line bg-raised px-10 type-body-sm text-ink disabled:bg-sunken"
                    />
                  </label>
                </div>
                <label className="flex flex-col gap-4 type-label-xs text-ink-muted">
                  PRESCRIPTION DIRECTIONS
                  <textarea
                    rows={3}
                    value={row.directions}
                    disabled={locked}
                    onChange={(event) =>
                      onChange({
                        directions: event.target.value,
                        directionTemplateId: "",
                        status: "pending",
                      })
                    }
                    className="border border-line bg-raised p-9 type-body-sm text-ink placeholder:text-ink-muted outline-none focus:shadow-focus resize-y disabled:bg-sunken"
                  />
                </label>
                <label className="flex flex-col gap-4 type-label-xs text-ink-muted">
                  PHYSICIAN NOTE
                  <textarea
                    rows={5}
                    value={row.physicianNote}
                    disabled={locked}
                    onChange={(event) =>
                      onChange({
                        physicianNote: event.target.value,
                        status: "pending",
                      })
                    }
                    className="border border-line bg-raised p-9 type-body-sm text-ink placeholder:text-ink-muted outline-none focus:shadow-focus resize-y disabled:bg-sunken"
                  />
                </label>
                {row.authorizationPolicy && (
                  <PrescriptionAuthorization
                    policy={row.authorizationPolicy}
                    refills={
                      row.authorizedRefills ?? row.authorizationPolicy.refills
                    }
                    confirmed={row.authorizationConfirmed === true}
                    disabled={locked}
                    onRefills={(value) =>
                      onChange({
                        authorizedRefills: value,
                        authorizationConfirmed: false,
                      })
                    }
                    onConfirm={(value) =>
                      onChange({ authorizationConfirmed: value })
                    }
                  />
                )}
                <label className="border border-warning/30 bg-warning-wash p-9 flex items-start gap-8 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={row.contraindicationsReviewed}
                    disabled={locked}
                    onChange={(event) =>
                      onChange({
                        contraindicationsReviewed: event.target.checked,
                        status: "pending",
                      })
                    }
                    className="mt-2"
                  />
                  <span>
                    <span className="type-label-sm text-warning">
                      Clinical attestation
                    </span>
                    <span className="block text-11 text-ink-secondary mt-2">
                      I reviewed the relevant intake, chart, current
                      medications, allergies, contraindications, and this
                      treatment with the patient during the synchronous visit.
                    </span>
                  </span>
                </label>
              </>
            )}
          </>
        )}

        {row.action === "skip" && (
          <p className="border border-line-subtle bg-sunken p-9 type-body-sm text-ink-muted">
            This paid item will remain in the normal clinical worklist so it
            cannot disappear accidentally.
          </p>
        )}
        {row.error && <p className="type-body-sm text-danger">{row.error}</p>}
        {!locked && validation && row.medicationProductId && (
          <p className="text-11 text-warning">Still needed: {validation}</p>
        )}
      </div>
    </article>
  );
}

function ActionButton({
  active,
  label,
  onClick,
}: {
  active: boolean;
  label: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      aria-pressed={active}
      onClick={onClick}
      className={`min-h-40 border px-9 type-label-sm ${
        active
          ? "border-ink bg-ink text-page"
          : "border-line bg-raised text-ink-muted hover:text-ink"
      }`}
    >
      {label}
    </button>
  );
}

function PatientContext({
  appointment,
  review,
  brief,
}: {
  appointment: AppointmentWorkItem;
  review: PostVisitReviewResponse;
  brief: PatientBriefResponse | undefined;
}) {
  return (
    <aside className="min-w-0 border-l border-line-subtle bg-sunken overflow-y-auto p-12 flex flex-col gap-12">
      <section className="border border-line-subtle bg-raised">
        <div className="p-9 border-b border-line-subtle flex items-center gap-6">
          <UserRound size={14} />
          <p className="type-label-sm">PATIENT</p>
        </div>
        <div className="p-9 flex flex-col gap-4">
          <p className="type-body-sm font-medium">{patientName(appointment)}</p>
          <p className="text-11 text-ink-muted break-all">
            {appointment.patient.email}
          </p>
          <p className="text-11 text-ink-muted">
            {[
              brief?.profile.age ? `${brief.profile.age} years` : null,
              brief?.profile.sex,
              brief?.profile.state ?? review.patient.state,
            ]
              .filter(Boolean)
              .join(" · ")}
          </p>
        </div>
      </section>

      <section className="border border-line-subtle bg-raised">
        <div className="p-9 border-b border-line-subtle flex items-center gap-6">
          <ShoppingBag size={14} />
          <p className="type-label-sm">PAID ITEMS AWAITING PRESCRIPTION</p>
        </div>
        <div className="divide-y divide-line-subtle">
          {review.purchased_items.length ? (
            review.purchased_items.map((item) => (
              <div key={item.order_id} className="p-9">
                <p className="type-body-sm font-medium">{item.label}</p>
                <p className="text-11 text-ink-muted mt-2">
                  {item.order_id} · {item.stage.replaceAll("_", " ")}
                </p>
              </div>
            ))
          ) : (
            <p className="p-9 type-body-sm text-ink-muted">
              No paid prescriptions are awaiting a decision.
            </p>
          )}
        </div>
      </section>

      {brief?.medications.length ? (
        <section className="border border-line-subtle bg-raised">
          <div className="p-9 border-b border-line-subtle">
            <p className="type-label-sm">ACTIVE CHART MEDICATIONS</p>
          </div>
          <div className="divide-y divide-line-subtle">
            {brief.medications.slice(0, 8).map((medication) => (
              <div key={medication.id} className="p-9">
                <p className="type-body-sm font-medium">{medication.name}</p>
                <p className="text-11 text-ink-muted mt-2">
                  {[medication.dose, medication.frequency, medication.route]
                    .filter(Boolean)
                    .join(" · ")}
                </p>
              </div>
            ))}
          </div>
        </section>
      ) : null}

      {review.authorizations.length > 0 && (
        <section className="border border-success/30 bg-success-wash p-9">
          <div className="flex items-center gap-6 text-success">
            <CheckCircle2 size={14} />
            <p className="type-label-sm">SAVED VISIT REVIEW ITEMS</p>
          </div>
          <div className="mt-7 flex flex-col gap-6">
            {review.authorizations.map((authorization) => (
              <div key={authorization.id}>
                <p className="type-body-sm font-medium">
                  {authorization.medication_name ||
                    authorization.purchase_product_id}
                </p>
                <p className="text-11 text-ink-muted">
                  {authorization.status.replaceAll("_", " ")}
                </p>
              </div>
            ))}
          </div>
        </section>
      )}
    </aside>
  );
}
